// ABOUTME: Dashboard-mode chart cell — renders the pre-aggregated current series + one overlay line per stored
// ABOUTME: comparison window, both loaded by the viewer (savedreportdata + comparison stores). No live query, no filter bar.
import { Box, ButtonBase, useTheme } from "@mui/material";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { tokens } from "../../../../theme";
import { ENV_TYPES } from "../../Sidebar";
import { RHBarChart } from "../charts/RHBarChart";
import { AreaChartContentReports } from "../../../theme/charts/AreaChart";
import { LineChartContentReports } from "../../../theme/charts/LineChart";
import { chartTypes, rhChartColorsDark, rhChartColorsLight } from "../chartFilterEnums";
import { reporthubChartOptions } from "../reports";
import { compareLabel } from "./RhRollingCompare";
import { dashboardCellKinds } from "../builder/dashboardEnums";
import Subheading from "../../../theme/text/Subheading";

// The chart components drop their legend into the flow below 1024px (position:static + order:2, i.e. under
// the chart). A dashboard cell's body has a fixed height, so a legend in the flow has nowhere to go and
// lands outside the card — it has to keep floating at every viewport. These keys match the components'
// own media queries so they replace them wholesale instead of losing the cascade to them.
const floatingLegendSx = {
    "@media screen and (max-width: 1024px)": {
        position: "absolute",
        order: 0,
        marginTop: -0,
        mr: 0,
        pr: 0.5,
    },
    "@media screen and (max-width: 768px)": {
        position: "absolute",
        marginTop: 0,
        mr: 0,
    },
};

// A cell too narrow to float the legend beside the title tucks it into the gap under the title instead: the
// 40px pill lifts out of the chart body so its top half hides in the header's 16px bottom margin and its
// bottom half floats over the plot's own headroom, the way the wide-cell legend already does. The body is
// inset by the same amount top and bottom, which buys the plot clearance from the pill and the card edge
// without the cell growing at all.
const BODY_INSET = 24;
const LEGEND_LIFT = BODY_INSET + 16;

const stackedLegendSx = {
    width: 1,
    mt: 0,
    top: -LEGEND_LIFT,
    left: 0,
    right: "unset",
    position: "absolute",
    inner: {
        width: 1,
    },
    ...floatingLegendSx,
};

// The bar container starts 16px lower than the line/area ones, so its legend has to lift that much further
// to land in the same place.
const stackedBarLegendSx = {
    ...stackedLegendSx,
    top: -(LEGEND_LIFT + 16),
};

// `data` is this report's pre-agg payload from the viewer:
//   { kind:"chart", config, chart:{ categories, series, displayType }, comparison:[{ start, end, data }] }.
export const RhDashboardChart = ({ chart, data, isTopRow, preview, sx }) => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const navigate = useNavigate();
    // Both environments run the same hub, only under their own route prefix.
    const selectedEnvironmentType = useSelector((state) => state.settings.environment.selectedEnvironmentType);
    const envPrefix = selectedEnvironmentType === ENV_TYPES[200] ? "/relation" : "/warehouse";
    const isDark = theme.palette.mode === "dark";
    const staticColors = isDark ? rhChartColorsDark : rhChartColorsLight;
    // Clicking the card drills into the full live report at /stockhubbi/report/:id.
    const reportId = chart?.reportId;

    // Read-only cell: the report config travels with the data; settings changes are inert.
    const currentReport = data?.config || null;
    const name = chart?.name || currentReport?.name;
    const displayType = data?.chart?.displayType || currentReport?.displayType || chart?.displayType;
    // In the builder preview there's no report config; give the bar chart a safe empty category shape so it
    // can render its loading skeleton without reading a null currentReport.
    const previewReport = currentReport || { category: { use: false, key: null, values: [] } };
    const chartOption = reporthubChartOptions.find((o) => o.name === (currentReport?.type || chart?.type));
    const categories = data?.chart?.categories || [];

    // Current series + one overlay line per stored comparison window (line/area only; a category bar has none).
    const values = [
        ...(data?.chart?.series || []),
        ...(data?.comparison || []).map((c) => ({ name: compareLabel(undefined, c, t, i18n.language), data: c.data || [] })),
    ];

    // The cell measures its own width.
    const cellRef = useRef(null);
    const [containerWidth, setContainerWidth] = useState(0);
    useEffect(() => {
        const el = cellRef.current;
        if (!el || typeof ResizeObserver === "undefined") return;
        const ro = new ResizeObserver(([entry]) => setContainerWidth(Math.round(entry.contentRect.width)));
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const large = !containerWidth || containerWidth >= 520;
    const small = containerWidth > 0 && containerWidth < 340;

    // Wide cells float the legend beside the title and need no clearance; no series means no legend at all.
    let bodyInset = 0;
    if (!large && values.length > 0) bodyInset = BODY_INSET;

    // The floating legend ends where the header row does: 40px short of the edge to clear the 32px drill-in
    // button and its gap, or flush with it on a cell that carries its own report and so has no button.
    let legendRight = 0;
    if (reportId != null) legendRight = 40;

    let bodyMinHeight = 330;
    if (preview) bodyMinHeight = 140;

    return (
        <Box
            ref={cellRef}
            sx={{
                height:1,
                flex: 1,
                display: "flex",
                flexDirection: "column",
                background: isTopRow ? theme.palette.mode === "dark" ? colors.grey[200] : chart?.kind === dashboardCellKinds.CHART && colors.grey[0] : null,
                borderRadius: 4,
                p: 2,
            }}
        >
            {/* The floating legend is lifted out of the chart body by a fixed amount tuned against the 32px
                drill-in button, so the header row keeps that height even when there's no button to hold it
                open (a cell carrying its own report has no id to drill into) — otherwise the row shrinks to
                the title's line box and the legend rides up out of line with it. */}
            {!preview && (
                <Box sx={{ width: 1, minHeight: 32, alignItems: "center", mb: 2, display: "flex" }}>
                    <Subheading sx={{ flex: 1 }}>{name}</Subheading>
                    {reportId != null && (
                        <ButtonBase
                            onClick={() => navigate(`${envPrefix}/stockhubbi/report/${reportId}`)}
                            sx={{
                                width: 32,
                                height: 32,
                                borderRadius: 5,
                                color: colors.txt["secondary"],
                                "&:hover": { background: isDark ? colors.grey[300] : colors.grey[100], color: colors.txt["primary"] },
                            }}
                        >
                            <ArrowOutwardIcon fontSize="small" />
                        </ButtonBase>
                    )}
                </Box>
            )}
            <Box sx={{ flex: 1, minHeight: bodyMinHeight, position: "relative" }}>
                {/* Absolute fill so the chart reads a concrete height and fills the cell. Narrow cells inset
                    it so the plot clears the legend above it and the card's edge below. */}
                <Box sx={{ position: "absolute", inset: 0, top: bodyInset, bottom: bodyInset }}>
                    {(data || preview) && (displayType === chartTypes.BAR ? (
                        <RHBarChart
                            values={values}
                            categories={categories}
                            chartSettings={chartOption}
                            chartOption={chartOption}
                            currentReport={preview ? previewReport : currentReport}
                            setCurrentReport={() => {}}
                            staticColors={staticColors}
                            isLoading={!!preview}
                            preview={preview}
                            isError={false}
                            onRetry={() => {}}
                            right={40}
                            top={-68}
                            sx={
                                large ? { legend: { right: legendRight, ...floatingLegendSx } } : {
                                    pr:0,
                                    flexDirection:"column",
                                    legend : stackedBarLegendSx
                                }
                            }
                        />
                    ) : displayType === chartTypes.AREA ? (
                        <AreaChartContentReports
                            values={values}
                            categories={categories}
                            chartSettings={chartOption}
                            staticColors={staticColors}
                            isLoading={!!preview}
                            preview={preview}
                            isError={false}
                            noSlant
                            height={"100%"}
                            smallLegend={small}
                            sx={{
                                legend: large ? {
                                    top: -52,
                                    right: legendRight,
                                    ...floatingLegendSx,
                                } : stackedLegendSx
                            }}
                        />
                    ) : (
                        <LineChartContentReports
                            values={values}
                            categories={categories}
                            chartSettings={chartOption}
                            staticColors={staticColors}
                            isLoading={!!preview}
                            preview={preview}
                            isError={false}
                            noSlant
                            height={"100%"}
                            smallLegend={small}
                            sx={{
                                legend: large ? {
                                    top: -76,
                                    right: legendRight,
                                    ...floatingLegendSx,
                                } : stackedLegendSx
                            }}
                            customContainerSx= {

                                large ? null : {
                                    mt:0,
                                }
                            }
                        />
                    ))}
                </Box>
            </Box>
        </Box>
    );
};
